import React, { createContext, useContext, useState, useEffect } from 'react';
import { DataRefreshService } from '@/src/services/DataRefresh';

export type CurrencyCode = 'USD' | 'EUR' | 'GBP' | 'INR' | 'JPY' | 'CAD' | 'AUD' | 'AED' | 'SGD' | 'CHF';

interface CurrencyInfo {
  code: CurrencyCode;
  symbol: string;
  locale: string;
  flag: string;
}

const CURRENCIES: Record<CurrencyCode, CurrencyInfo> = {
  USD: { code: 'USD', symbol: '$', locale: 'en-US', flag: '🇺🇸' },
  EUR: { code: 'EUR', symbol: '€', locale: 'de-DE', flag: '🇪🇺' },
  GBP: { code: 'GBP', symbol: '£', locale: 'en-GB', flag: '🇬🇧' },
  INR: { code: 'INR', symbol: '₹', locale: 'en-IN', flag: '🇮🇳' },
  JPY: { code: 'JPY', symbol: '¥', locale: 'ja-JP', flag: '🇯🇵' },
  CAD: { code: 'CAD', symbol: '$', locale: 'en-CA', flag: '🇨🇦' },
  AUD: { code: 'AUD', symbol: '$', locale: 'en-AU', flag: '🇦🇺' },
  AED: { code: 'AED', symbol: 'د.إ', locale: 'ar-AE', flag: '🇦🇪' },
  SGD: { code: 'SGD', symbol: '$', locale: 'en-SG', flag: '🇸🇬' },
  CHF: { code: 'CHF', symbol: 'Fr', locale: 'de-CH', flag: '🇨🇭' },
};

interface CurrencyContextType {
  currency: CurrencyInfo;
  setCurrency: (code: CurrencyCode) => void;
  currencies: CurrencyInfo[];
  formatCurrency: (value: number) => string;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const [currencyCode, setCurrencyCode] = useState<CurrencyCode>(() => {
    const saved = localStorage.getItem('fin-empire-currency');
    if (saved && saved in CURRENCIES) return saved as CurrencyCode;
    
    // Try to guess from locale
    const locale = navigator.language;
    if (locale.includes('IN')) return 'INR';
    if (locale.includes('GB')) return 'GBP';
    if (locale.includes('JP')) return 'JPY';
    if (locale.includes('DE') || locale.includes('FR') || locale.includes('IT')) return 'EUR';
    
    return 'USD';
  });

  const [rates, setRates] = useState<Record<string, number>>({});

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const latest = await DataRefreshService.getLatestExchangeRates('USD');
        if (latest) {
          setRates(latest);
        }
      } catch {
        // Silent and defensive safeguard to avoid console warnings or unhandled rejections
      }
    };
    fetchRates();
  }, []);

  const currency = CURRENCIES[currencyCode];

  useEffect(() => {
    localStorage.setItem('fin-empire-currency', currencyCode);
  }, [currencyCode]);

  const setCurrency = (code: CurrencyCode) => {
    setCurrencyCode(code);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(currency.locale, {
      style: 'currency',
      currency: currency.code,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, currencies: Object.values(CURRENCIES), formatCurrency }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
